import React, { useMemo, useState } from 'react'
import {
  Combobox,
  COMBOBOX_DEMO_OPTIONS,
  COMBOBOX_PREVIEW_STATE_LABELS,
  COMBOBOX_PREVIEW_STATES,
  type ComboboxPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ComboboxShowcase.css'

const PROVIDER_OPTIONS = [
  { value: 'Dr. Ava Smith', label: 'Dr. Ava Smith' },
  { value: 'Dr. Marcus Jones', label: 'Dr. Marcus Jones' },
  { value: 'Dr. Priya Lee', label: 'Dr. Priya Lee' },
  { value: 'Dr. Daniel Chen', label: 'Dr. Daniel Chen' },
  { value: 'Dr. Sofia Patel', label: 'Dr. Sofia Patel' },
]

const ComboboxShowcase: React.FC = () => {
  const [chartName, setChartName] = useState('')
  const [providerName, setProviderName] = useState('')
  const [label, setLabel] = useState('Chart name')
  const [disabled, setDisabled] = useState(false)
  const [optional, setOptional] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [allowCustom, setAllowCustom] = useState(true)
  const [filterable, setFilterable] = useState(true)
  const [error, setError] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [helperText, setHelperText] = useState('')
  const [highlightedState, setHighlightedState] =
    useState<ComboboxPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'combobox-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="combobox-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as ComboboxPreviewState,
                  )
                }
              >
                {COMBOBOX_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {COMBOBOX_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'combobox-label',
            label: 'Chart name label',
            control: (
              <input
                id="combobox-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'combobox-helper-text',
            label: 'Helper text',
            control: (
              <input
                id="combobox-helper-text"
                className="ui-field-control"
                type="text"
                value={helperText}
                placeholder="Optional helper text"
                onChange={(event) => setHelperText(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'combobox-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="combobox-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'combobox-optional',
            label: 'Optional',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="combobox-optional"
                  type="checkbox"
                  checked={optional}
                  onChange={(event) => setOptional(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'combobox-tooltip',
            label: 'Tooltip',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="combobox-tooltip"
                  type="checkbox"
                  checked={showTooltip}
                  onChange={(event) => setShowTooltip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'combobox-allow-custom',
            label: 'Allow custom',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="combobox-allow-custom"
                  type="checkbox"
                  checked={allowCustom}
                  onChange={(event) => setAllowCustom(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'combobox-filterable',
            label: 'Filterable',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="combobox-filterable"
                  type="checkbox"
                  checked={filterable}
                  onChange={(event) => setFilterable(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'combobox-error',
            label: 'Error',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="combobox-error"
                  type="checkbox"
                  checked={error}
                  onChange={(event) => setError(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'combobox-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="combobox-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      allowCustom,
      darkMode,
      disabled,
      error,
      filterable,
      helperText,
      highlightedState,
      label,
      optional,
      showTooltip,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Combobox"
        description="Type-or-select field with label, filtering, custom values, helper text, and the same chrome as Input field and Select."
        stageClassName="ui-preview__stage--wide combobox-showcase__stage-wrap"
      >
        <div
          className={`combobox-showcase__sheet${
            darkMode ? ' combobox-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Type a custom value or pick from the dropdown. Property controls
              apply to both examples.
            </p>
            <div className="combobox-showcase__live-grid">
              <Combobox
                label={label}
                placeholder="Type or select"
                value={chartName}
                options={COMBOBOX_DEMO_OPTIONS}
                disabled={disabled}
                optional={optional}
                showTooltip={showTooltip}
                allowCustom={allowCustom}
                filterable={filterable}
                darkMode={darkMode}
                error={error}
                helperText={helperText || undefined}
                onChange={setChartName}
              />
              <Combobox
                label="Provider name"
                placeholder="Type or select"
                value={providerName}
                options={PROVIDER_OPTIONS}
                disabled={disabled}
                optional={optional}
                showTooltip={showTooltip}
                allowCustom={allowCustom}
                filterable={filterable}
                darkMode={darkMode}
                error={error}
                helperText={helperText || undefined}
                onChange={setProviderName}
              />
            </div>
          </section>

          <section className="combobox-showcase__matrix">
            {COMBOBOX_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`combobox-showcase__row${
                  previewState === highlightedState
                    ? ' combobox-showcase__row--selected'
                    : ''
                }${
                  previewState === 'expanded'
                    ? ' combobox-showcase__row--expanded'
                    : ''
                }${darkMode ? ' combobox-showcase__row--dark' : ''}`}
              >
                <h4 className="combobox-showcase__label">
                  {COMBOBOX_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <Combobox previewState={previewState} darkMode={darkMode} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Combobox" sections={propertySections} />
    </>
  )
}

export default ComboboxShowcase
